import type { TicketCardRow } from '../api/easeedesk'
import { agoOrEmpty, durationString, hoursString, truncText } from './format'

/**
 * Randul de sus al fisei - `easeedesk/ticket/details.php` cu
 * `TicketTable.buildTable()` din `public/js/easeedesk/easeedesk.js`.
 *
 * Aceleasi 17 coloane, in aceeasi ordine, cu aceleasi titluri si aceleasi
 * clase. Coloana "Actions" ramane goala: actiunile sunt calea de SCRIERE si nu
 * se muta in pasul asta - dar coloana se deseneaza, altfel capul de tabel si
 * randul n-ar mai avea acelasi numar de celule si tabelul s-ar strica.
 */

/* Titlurile din `easeedesk/ticket/details.php`, in ordinea de acolo. */
const COLUMNS = [
  '#',
  'Type',
  'Subject',
  'Company',
  'Office',
  'Affected User/Device',
  'Priority',
  'Category',
  'SLA',
  'Created by',
  'Source',
  'Assigned Group',
  'Assignee',
  'Created',
  'Updated',
  'Status',
  'Actions',
]

const MARK_ICON = {
  chat: { icon: 'fa fa-comment color-red font-14', title: (n: number) => `${n} new chat messages` },
  conversation: { icon: 'fa fa-envelope font-14 color-warning', title: (n: number) => `${n} new conversation messages` },
  comments: { icon: 'far fa-comments font-14 color-red', title: (n: number) => `${n} new comments` },
}

export function TicketHeaderTable({ row, now }: { row: TicketCardRow; now?: Date }) {
  return (
    <table className="table table-striped table-hover table-bordered dataTable dataTableM" id="ticketTable" width="100%">
      <thead>
        <tr>
          {COLUMNS.map((title) => (
            <th key={title}>{title}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>{row.id}</strong>
          </td>
          <td>
            <span className="badge ticket-type-badge" data-toggle="tooltip" data-placement="top" title={row.type.name} style={{ backgroundColor: row.type.color }}>
              {row.type.short}
            </span>
          </td>
          <td>
            <Mark row={row} />
            {row.attachments > 0 && (
              <>
                <i className="fa fa-paperclip" aria-hidden="true" data-toggle="tooltip" data-placement="top" title={`${row.attachments} Attachments`}></i>{' '}
              </>
            )}
            {row.subject}
          </td>
          <td>
            <a href="#" className="show-company-details">
              <i className="fas fa-th-large" aria-hidden="true"></i> {row.company}
            </a>
          </td>
          <td>{row.office}</td>
          <Affected row={row} />
          <td>
            {row.priority.id > 0 && (
              <span className="label" style={{ backgroundColor: row.priority.color }}>
                {row.priority.name}
              </span>
            )}
          </td>
          <td>{row.category !== '' && <span>{row.category}</span>}</td>
          <Sla row={row} />
          <td>{row.creator}</td>
          <td>
            {row.source.icon !== '' && (
              <label data-toggle="tooltip" data-placement="top" title={row.source.name} className="label label-source" style={{ backgroundColor: row.source.color }}>
                <i className={row.source.icon} aria-hidden="true"></i>
              </label>
            )}
          </td>
          <td>
            {row.group !== '' && (
              <a href="#" className="show-group-details">
                {row.group}
              </a>
            )}
          </td>
          <td>
            {row.assignee.name === '' ? (
              <label className="label label-danger">Unassigned</label>
            ) : (
              <>
                {row.assignee.name}
                {row.assignee.takeOver !== '' && <span className="color-red"> Take over: {row.assignee.takeOver}</span>}
              </>
            )}
          </td>
          <td>{agoOrEmpty(row.created, now)}</td>
          <td>{agoOrEmpty(row.updated, now)}</td>
          <td className="center" style={{ backgroundColor: row.status.color }}>
            <label className="label ticket-status-label">{row.status.name}</label>
          </td>
          <td className="center"></td>
        </tr>
      </tbody>
    </table>
  )
}

/** O singura insigna inaintea subiectului - cea pe care a ales-o serverul. */
function Mark({ row }: { row: TicketCardRow }) {
  if (!row.mark) return null
  const mark = MARK_ICON[row.mark.kind]
  return (
    <>
      <i className={mark.icon} data-toggle="tooltip" data-placement="top" title={mark.title(row.mark.count)} aria-hidden="true"></i>{' '}
    </>
  )
}

/**
 * Coloana "Affected User/Device". Pe un tichet deschis pe MASINI se scrie cate
 * sunt; pe unul deschis pe un om, numele lui, taiat la 30 de caractere.
 */
function Affected({ row }: { row: TicketCardRow }) {
  if (row.affected.text === '') return <td></td>

  if (row.affected.category === 2) {
    return (
      <td>
        <a href="#" className="show-affected-details">
          <i className="fas fa-exclamation-triangle color-warning" aria-hidden="true"></i> {row.affected.text} devices
        </a>
      </td>
    )
  }

  return (
    <td>
      <a href="#" title={row.affected.text}>
        <i className="fas fa-exclamation-triangle color-warning" aria-hidden="true"></i> {truncText(row.affected.text, 30)}
      </a>
    </td>
  )
}

/**
 * Celula SLA: termenul si cat s-a consumat din el, rosu cand a fost depasit.
 *
 * Serverul spune DACA e depasit; aici se scriu doar cifrele, cu aceiasi doi
 * formatori ca in portal - termenul cu `FormatSecondsAsDurationString`, timpul
 * consumat cu `FormatSecondsAsHoursString`.
 */
function Sla({ row }: { row: TicketCardRow }) {
  if (row.slaClock && row.slaClock.sla > 0) return <SlaClockCell clock={row.slaClock} closed={row.closed} />
  if (row.sla.seconds === 0) return <td></td>

  return (
    <td>
      <i className="far fa-handshake" aria-hidden="true"></i> {durationString(row.sla.seconds)}
      <div style={{ color: row.sla.breached ? 'red' : 'green' }}>
        <i className="far fa-clock" aria-hidden="true"></i> {hoursString(row.sla.activeSeconds)}
      </div>
    </td>
  )
}

/**
 * Celula SLA cand tichetul are profil: aceleasi doua cifre, masurate dupa
 * calendar, plus termenul ("due ...") sau "paused" cand ceasul e oprit, si
 * numele profilului in tooltip.
 */
function SlaClockCell({ clock, closed }: { clock: NonNullable<TicketCardRow['slaClock']>; closed: boolean }) {
  const title = `${clock.mode === 'business' ? 'Business hours' : '24/7'}${clock.profile ? ` - ${clock.profile}` : ''}`

  return (
    <td className="od-sla-clock" title={title}>
      <i className="far fa-handshake" aria-hidden="true"></i> {durationString(clock.sla)}
      {clock.mode === 'business' ? (
        <>
          {' '}
          <i className="fas fa-business-time" aria-hidden="true"></i>
        </>
      ) : null}
      <div style={{ color: clock.breached ? 'red' : 'green' }}>
        <i className="far fa-clock" aria-hidden="true"></i> {hoursString(clock.elapsed)}
      </div>
      {closed ? null : clock.breached ? (
        <div className="od-sla-due" style={{ color: 'red' }}>
          over by {hoursString(-clock.remaining)}
        </div>
      ) : !clock.running ? (
        <div className="od-sla-due text-muted">paused</div>
      ) : clock.due_local ? (
        <div className="od-sla-due" title={clock.timezone ?? ''}>
          due {clock.due_local} · {hoursString(clock.remaining)} left
        </div>
      ) : null}
    </td>
  )
}
